/*
 * Lealtad: lo que comparten las pantallas del módulo.
 *
 * - Valores por defecto y armado del `settings` del programa.
 * - Conversión de las reglas de campaña entre lo que edita la pantalla y el
 *   JSON que se guarda (el formato está en discover-api/docs/loyalty/engine-contract.md).
 * - Formato de puntos, montos y errores.
 */

import { getActiveCompany } from '@/helpers/companySession'

/**
 * El módulo se contrata por cuenta (`company.products.loyalty`). Sin eso las
 * pantallas de lealtad muestran la invitación a activarlo (views/loyalty/locked).
 */
export function companyHasLoyalty() {
  let role = null
  try {
    role = JSON.parse(localStorage.getItem('auth.currentUser')).user.role
  } catch (e) {
    role = null
  }
  if (role === 'super_admin' || role === 'superadmin') return true
  const company = getActiveCompany()
  return !!(company && company.products && company.products.loyalty === true)
}

export function uuid() {
  const c = typeof window !== 'undefined' ? window.crypto : null
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0'))
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export function clone(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value))
}

/* ───────────── Programa ───────────── */

export function defaultSettings() {
  return {
    currency: 'PEN',
    timezone: 'America/Lima',
    labels: { points: 'Puntos', currency: '' },
    earn: {
      enabled: true,
      points: 1,
      perAmount: 1,
      rounding: 'floor',
      base: {
        includeTax: true,
        includeShipping: false,
        includeTips: false,
        includePaidWithPoints: false
      },
      minOrderAmount: 0,
      maxPointsPerOrder: null,
      creditDelayHours: 0
    },
    redeem: {
      enabled: true,
      mode: 'monetary',
      pointValue: 0.01,
      step: 1,
      minPoints: 0,
      maxPointsPerOrder: null,
      maxPctOfOrder: 100,
      appliesTo: { shipping: false },
      combinable: { promotions: true, coupons: true },
      countsTowardsMinimum: true,
      holdTtlMinutes: 30
    },
    expiry: { mode: 'per_lot', months: 6, endOfDay: true },
    reversal: { revokeEarned: true, restoreRedeemed: true, restoredExpiry: 'original' },
    levels: { enabled: false, metric: 'points', basis: 'lifetime', windowMonths: 12 },
    rewards: {
      enabled: true,
      maxPerOrder: 1,
      combinable: { points: true, promotions: true, coupons: true },
      codePrefix: ''
    },
    identity: { key: 'email' },
    browser: { enabled: false }
  }
}

function mergeInto(target, source) {
  if (!isPlainObject(source)) return target
  Object.keys(source).forEach(key => {
    const value = source[key]
    if (isPlainObject(value) && isPlainObject(target[key])) {
      mergeInto(target[key], value)
    } else if (value !== undefined) {
      target[key] = value
    }
  })
  return target
}

/** Lo guardado sobre los valores por defecto: el formulario siempre tiene todos los campos. */
export function mergeSettings(saved) {
  return mergeInto(defaultSettings(), clone(saved) || {})
}

// Campos numéricos del formulario. `nullable`: vacío significa "sin tope".
const NUMBER_FIELDS = [
  ['earn.points'], ['earn.perAmount'], ['earn.minOrderAmount'], ['earn.maxPointsPerOrder', true], ['earn.creditDelayHours'],
  ['redeem.pointValue'], ['redeem.step'], ['redeem.minPoints'], ['redeem.maxPointsPerOrder', true], ['redeem.maxPctOfOrder'], ['redeem.holdTtlMinutes'],
  ['expiry.months'], ['levels.windowMonths'], ['rewards.maxPerOrder']
]

export function getPath(object, path) {
  return String(path).split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), object)
}

function setPath(object, path, value) {
  const keys = String(path).split('.')
  const last = keys.pop()
  const parent = keys.reduce((acc, key) => acc[key], object)
  parent[last] = value
}

export function toNumberOrNull(value) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

/** El `settings` que se envía: números como números y los topes vacíos como `null`. */
export function settingsToPayload(form) {
  const settings = mergeSettings(form)
  const defaults = defaultSettings()
  NUMBER_FIELDS.forEach(([path, nullable]) => {
    const number = toNumberOrNull(getPath(settings, path))
    setPath(settings, path, number === null ? (nullable ? null : getPath(defaults, path)) : number)
  })
  settings.currency = String(settings.currency || '').trim().toUpperCase()
  settings.labels.points = String(settings.labels.points || '').trim()
  settings.labels.currency = String(settings.labels.currency || '').trim()
  settings.rewards.codePrefix = String(settings.rewards.codePrefix || '').trim().toUpperCase()
  return settings
}

/* ───────────── Formato ───────────── */

export function formatNumber(value, locale, maxDecimals = 2) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '-'
  try {
    return new Intl.NumberFormat(locale || undefined, {
      minimumFractionDigits: Number.isInteger(number) ? 0 : Math.min(2, maxDecimals),
      maximumFractionDigits: maxDecimals
    }).format(number)
  } catch (e) {
    return String(number)
  }
}

export function formatPoints(value, locale) {
  return formatNumber(value, locale, 0)
}

/**
 * Monto con la moneda del programa: usa el símbolo configurado
 * (`labels.currency`) y, si no hay, el código de moneda.
 */
export function formatMoney(value, settings, locale, maxDecimals = 2) {
  const number = Number(value)
  if (!Number.isFinite(number)) return '-'
  const symbol = settings && settings.labels && settings.labels.currency
  const code = settings && settings.currency
  const amount = formatNumber(number, locale, maxDecimals)
  if (symbol) return `${symbol} ${amount}`
  if (code) return `${code} ${amount}`
  return amount
}

export function formatDateTime(value, locale) {
  if (!value) return '-'
  const date = new Date(value)
  if (isNaN(date.getTime())) return '-'
  try {
    return date.toLocaleString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch (e) {
    return date.toISOString()
  }
}

export function formatDate(value, locale) {
  if (!value) return '-'
  const date = new Date(value)
  if (isNaN(date.getTime())) return '-'
  try {
    return date.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' })
  } catch (e) {
    return date.toISOString().slice(0, 10)
  }
}

/** Rango de fechas del selector → inicio y fin de día, en ISO. */
export function dateRangeToQuery(range) {
  if (!Array.isArray(range) || !range[0] || !range[1]) return {}
  const from = new Date(range[0])
  from.setHours(0, 0, 0, 0)
  const to = new Date(range[1])
  to.setHours(23, 59, 59, 999)
  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * Resumen del programa en frases cortas.
 * `t` es `$t` (ya enlazado) y `locale` el idioma activo.
 */
export function programSummary(form, t, locale) {
  const s = settingsToPayload(form)
  const money = value => formatMoney(value, s, locale)
  const points = value => formatPoints(value, locale)
  const label = (s.labels.points || t('loyalty.common.points')).toLowerCase()
  const lines = []

  if (!s.earn.enabled) {
    lines.push(t('loyalty.program.summary_earn_off'))
  } else {
    lines.push(t('loyalty.program.summary_earn', { points: points(s.earn.points), label, amount: money(s.earn.perAmount) }))
    const included = []
    if (s.earn.base.includeTax) included.push(t('loyalty.program.summary_base_tax'))
    if (s.earn.base.includeShipping) included.push(t('loyalty.program.summary_base_shipping'))
    if (s.earn.base.includeTips) included.push(t('loyalty.program.summary_base_tips'))
    if (included.length) lines.push(t('loyalty.program.summary_base', { list: included.join(', ') }))
    if (!s.earn.base.includePaidWithPoints) lines.push(t('loyalty.program.summary_base_no_points'))
    if (s.earn.minOrderAmount > 0) lines.push(t('loyalty.program.summary_earn_min', { amount: money(s.earn.minOrderAmount) }))
    if (s.earn.maxPointsPerOrder !== null) lines.push(t('loyalty.program.summary_earn_max', { points: points(s.earn.maxPointsPerOrder), label }))
    if (s.earn.creditDelayHours > 0) lines.push(t('loyalty.program.summary_earn_delay', { hours: formatNumber(s.earn.creditDelayHours, locale) }))
  }

  if (!s.redeem.enabled) {
    lines.push(t('loyalty.program.summary_redeem_off'))
  } else {
    lines.push(t('loyalty.program.summary_redeem', { amount: formatMoney(s.redeem.pointValue, s, locale, 4) }))
    if (s.redeem.minPoints > 0) lines.push(t('loyalty.program.summary_redeem_min', { points: points(s.redeem.minPoints), label }))
    if (s.redeem.step > 1) lines.push(t('loyalty.program.summary_redeem_step', { points: points(s.redeem.step) }))
    if (s.redeem.maxPctOfOrder < 100) lines.push(t('loyalty.program.summary_redeem_pct', { pct: formatNumber(s.redeem.maxPctOfOrder, locale) }))
    if (s.redeem.maxPointsPerOrder !== null) lines.push(t('loyalty.program.summary_redeem_max', { points: points(s.redeem.maxPointsPerOrder), label }))
    if (s.redeem.appliesTo.shipping) lines.push(t('loyalty.program.summary_redeem_shipping'))
    if (!s.redeem.combinable.coupons && !s.redeem.combinable.promotions) lines.push(t('loyalty.program.summary_no_combine_both'))
    else if (!s.redeem.combinable.coupons) lines.push(t('loyalty.program.summary_no_combine_coupons'))
    else if (!s.redeem.combinable.promotions) lines.push(t('loyalty.program.summary_no_combine_promotions'))
  }

  if (s.expiry.mode === 'none') {
    lines.push(t('loyalty.program.summary_expiry_none'))
  } else {
    lines.push(t(s.expiry.months === 1 ? 'loyalty.program.summary_expiry_one' : 'loyalty.program.summary_expiry', { months: formatNumber(s.expiry.months, locale) }))
  }

  if (s.levels.enabled) {
    const metric = t(`loyalty.program.levels_metric_${s.levels.metric}`).toLowerCase()
    lines.push(s.levels.basis === 'window'
      ? t('loyalty.program.summary_levels_window', { metric, months: formatNumber(s.levels.windowMonths, locale) })
      : t('loyalty.program.summary_levels_lifetime', { metric }))
  }

  if (s.reversal.revokeEarned && s.reversal.restoreRedeemed) lines.push(t('loyalty.program.summary_reversal_both'))
  else if (s.reversal.revokeEarned) lines.push(t('loyalty.program.summary_reversal_revoke'))
  else if (s.reversal.restoreRedeemed) lines.push(t('loyalty.program.summary_reversal_restore'))
  else lines.push(t('loyalty.program.summary_reversal_none'))

  return lines
}

/* ───────────── Errores ───────────── */

/**
 * Los endpoints que consultan saldos y movimientos responden 200 con
 * `{ error: { code, message } }` cuando no pueden atender el pedido.
 */
export function responseError(data) {
  if (data && isPlainObject(data.error)) {
    return { isLoyaltyError: true, code: data.error.code || null, message: data.error.message || '', details: data.error.details }
  }
  return null
}

/** Texto para mostrar de un error, venga del cuerpo 200 o de un HTTP 4xx/5xx. */
export function errorMessage(error, t, fallback) {
  const body = error && error.response && error.response.data
  const code = (error && error.isLoyaltyError && error.code) || (body && body.error && body.error.code)
  if (code && t) {
    const key = `loyalty.errors.${code}`
    const translated = t(key)
    if (translated && translated !== key) return translated
  }
  if (error && error.isLoyaltyError && error.message) return error.message
  if (body && body.error && body.error.message) return body.error.message
  if (body && typeof body.message === 'string' && body.message) return body.message
  return fallback || ''
}

/** Errores de validación de un guardado (HTTP 400 con `errors: [{ path, code, message }]`). */
export function validationErrors(error) {
  const body = error && error.response && error.response.data
  if (!body || !Array.isArray(body.errors)) return []
  return body.errors
    .filter(e => e && (e.message || e.code))
    .map(e => ({ path: String(e.path || ''), code: e.code || '', message: e.message || e.code }))
}

/** `settings.earn.points` y `earn.points` son el mismo campo del formulario. */
export function settingsPath(path) {
  return String(path || '').replace(/^program\./, '').replace(/^settings\./, '')
}

/* ───────────── Reglas de campaña ───────────── */

export const EFFECT_TYPES = ['points_fixed', 'points_multiplier', 'points_per_amount', 'reward']

// Cuándo corre una campaña: con una compra o con algo que hace el cliente fuera de comprar.
export const TRIGGERS = ['purchase', 'signup', 'referral', 'event']
export const STREAK_UNITS = ['day', 'week', 'month']

const NUMBER_OPERATORS = ['>', '>=', '<', '<=', '=']
const LIST_OPERATORS = ['in', 'not_in']

/** Campos de condición que se ofrecen: los de la tabla del contrato, ni uno más. */
export const CONDITION_FIELDS = {
  purchase_value: { kind: 'number', group: 'purchase', operators: NUMBER_OPERATORS },
  order_items: { kind: 'items', group: 'purchase', operators: LIST_OPERATORS },
  channel: { kind: 'strings', group: 'purchase', operators: LIST_OPERATORS },
  store: { kind: 'strings', group: 'purchase', operators: LIST_OPERATORS },
  payment_method: { kind: 'strings', group: 'purchase', operators: LIST_OPERATORS },
  first_purchase: { kind: 'boolean', group: 'customer', operators: ['='] },
  order_count: { kind: 'number', group: 'customer', operators: NUMBER_OPERATORS },
  streak: { kind: 'streak', group: 'customer', operators: [] },
  orders_in_period: { kind: 'period_count', group: 'customer', operators: NUMBER_OPERATORS },
  spend_in_period: { kind: 'period_amount', group: 'customer', operators: NUMBER_OPERATORS },
  membership: { kind: 'levels', group: 'customer', operators: LIST_OPERATORS },
  customer_field: { kind: 'property', group: 'customer', operators: ['=', '!=', 'like', 'not_like', '>', '<', 'empty', 'not_empty'] },
  dates: { kind: 'date', group: 'time', operators: ['<', '>', '='] },
  days: { kind: 'days', group: 'time', operators: [] },
  hours_min: { kind: 'hours', group: 'time', operators: [] }
}

export const CONDITION_GROUPS = ['purchase', 'customer', 'time']

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?/
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

function operatorId(operator) {
  if (isPlainObject(operator)) return operator.id
  return operator
}

function stringList(values) {
  if (!Array.isArray(values)) return []
  const out = []
  values.forEach(v => {
    const text = String(isPlainObject(v) ? (v.id ?? v.value ?? v.name ?? '') : (v ?? '')).trim()
    if (text && !out.includes(text)) out.push(text)
  })
  return out
}

function booleanValue(value) {
  const raw = isPlainObject(value) ? value.id : value
  if (raw === true || raw === 'true') return true
  if (raw === false || raw === 'false') return false
  return null
}

/**
 * Una condición en el formato exacto que se guarda, o `null` si el campo no
 * está entre los soportados. Acepta también lo que guardaba la pantalla
 * anterior (operador como objeto, niveles y días con datos de más, etc.).
 */
export function normalizeCondition(node) {
  if (!node || !CONDITION_FIELDS[node.field]) return null
  const def = CONDITION_FIELDS[node.field]
  let operator = operatorId(node.operator)
  if (def.operators.length && !def.operators.includes(operator)) operator = def.operators[0]
  const term = node.term

  switch (def.kind) {
    case 'number':
      return { field: node.field, operator, term: toNumberOrNull(term) }
    case 'items': {
      const source = isPlainObject(term) ? term : {}
      const quantity = Math.floor(Number(source.minQuantity))
      return {
        field: node.field,
        operator,
        term: {
          by: String(source.by || 'id'),
          values: stringList(source.values),
          minQuantity: Number.isFinite(quantity) && quantity >= 1 ? quantity : 1
        }
      }
    }
    case 'strings':
      return { field: node.field, operator, term: stringList(term) }
    case 'boolean':
      return { field: node.field, operator: '=', term: booleanValue(term) }
    case 'levels': {
      const ids = []
      ;(Array.isArray(term) ? term : []).forEach(level => {
        const id = isPlainObject(level) ? level._id : level
        if (id && !ids.includes(String(id))) ids.push(String(id))
      })
      return { field: node.field, operator, term: ids.map(_id => ({ _id })) }
    }
    case 'property': {
      // La pantalla anterior guardaba la propiedad aparte, en `customer_field`.
      let property = ''
      let value = null
      if (isPlainObject(term) && 'field' in term) {
        property = term.field
        value = term.value
      } else if (isPlainObject(node.customer_field)) {
        property = node.customer_field.column_name
        value = isPlainObject(term) && 'id' in term ? term.id : term
      }
      if (isPlainObject(value) || Array.isArray(value)) value = null
      if (operator === 'empty' || operator === 'not_empty') value = null
      else if ((operator === '>' || operator === '<') && value !== null && value !== '' && Number.isFinite(Number(value))) value = Number(value)
      else if (value === undefined) value = null
      return { field: node.field, operator, term: { field: String(property || '').trim(), value } }
    }
    case 'date': {
      const match = typeof term === 'string' ? term.match(DATE_PATTERN) : null
      return { field: node.field, operator, term: match ? match[0] : null }
    }
    case 'days': {
      const ids = []
      ;(Array.isArray(term) ? term : []).forEach(day => {
        const id = Number(isPlainObject(day) ? day.id : day)
        if (Number.isInteger(id) && id >= 0 && id <= 6 && !ids.includes(id)) ids.push(id)
      })
      return { field: node.field, term: ids.sort((a, b) => a - b).map(id => ({ id })) }
    }
    case 'hours': {
      const range = Array.isArray(term) ? term : []
      return { field: node.field, term: [String(range[0] || ''), String(range[1] || '')] }
    }
    case 'streak': {
      const source = isPlainObject(term) ? term : {}
      const periods = Math.floor(Number(source.periods))
      return {
        field: node.field,
        term: {
          periods: Number.isFinite(periods) && periods >= 1 ? periods : null,
          unit: STREAK_UNITS.includes(source.unit) ? source.unit : 'week',
          repeat: source.repeat === true
        }
      }
    }
    case 'period_count':
    case 'period_amount': {
      const source = isPlainObject(term) ? term : {}
      const key = def.kind === 'period_count' ? 'count' : 'amount'
      return { field: node.field, operator, term: { [key]: toNumberOrNull(source[key]), days: toNumberOrNull(source.days) } }
    }
  }
  return null
}

/** ¿La condición (ya normalizada) tiene todo lo que necesita? */
export function isConditionComplete(condition) {
  if (!condition) return false
  const def = CONDITION_FIELDS[condition.field]
  if (!def) return false
  const term = condition.term
  switch (def.kind) {
    case 'number': return term !== null
    case 'items': return !!term.by && term.values.length > 0
    case 'strings': return term.length > 0
    case 'boolean': return term === true || term === false
    case 'levels': return term.length > 0
    case 'property':
      if (!term.field) return false
      if (condition.operator === 'empty' || condition.operator === 'not_empty') return true
      return term.value !== null && term.value !== ''
    case 'date': return !!term
    case 'days': return term.length > 0
    case 'hours': return TIME_PATTERN.test(term[0]) && TIME_PATTERN.test(term[1])
    case 'streak': return term.periods !== null
    case 'period_count': return term.count !== null && term.days > 0
    case 'period_amount': return term.amount !== null && term.days > 0
  }
  return false
}

function isGroup(node) {
  return !!node && Array.isArray(node.children)
}

/**
 * Condiciones guardadas → lo que edita la pantalla. Devuelve también cuántas
 * condiciones se quitaron por no estar soportadas.
 */
export function conditionsToEditable(stored) {
  let dropped = 0
  const walk = (group, isRoot) => {
    const out = { type: group && group.type === 'or' ? 'or' : 'and', children: [] }
    if (!isRoot) out.group = true
    ;((group && group.children) || []).forEach(child => {
      if (isGroup(child)) {
        const nested = walk(child, false)
        if (nested.children.length) out.children.push(nested)
        return
      }
      if (!child || !child.field) return
      const condition = normalizeCondition(child)
      if (!condition) {
        dropped++
        return
      }
      if (!('operator' in condition)) condition.operator = null
      out.children.push(condition)
    })
    return out
  }
  return { conditions: walk(isGroup(stored) ? stored : { type: 'and', children: [] }, true), dropped }
}

/** Lo que edita la pantalla → el JSON que se guarda. */
export function conditionsToStored(editable) {
  const walk = group => {
    const out = { type: group && group.type === 'or' ? 'or' : 'and', children: [] }
    ;((group && group.children) || []).forEach(child => {
      if (isGroup(child)) {
        const nested = walk(child)
        if (nested.children.length) out.children.push(nested)
        return
      }
      const condition = normalizeCondition(child)
      if (condition) out.children.push(condition)
    })
    return out
  }
  return walk(editable)
}

export function countConditions(conditions) {
  let total = 0
  const walk = group => ((group && group.children) || []).forEach(child => (isGroup(child) ? walk(child) : total++))
  walk(conditions)
  return total
}

function hasIncompleteCondition(conditions) {
  let incomplete = false
  const walk = group => ((group && group.children) || []).forEach(child => {
    if (isGroup(child)) return walk(child)
    if (!isConditionComplete(normalizeCondition(child))) incomplete = true
  })
  walk(conditions)
  return incomplete
}

function normalizeEffect(rule) {
  const effect = isPlainObject(rule.effect) ? rule.effect : null
  if (effect && effect.type === 'reward') {
    const rewardId = effect.rewardId ?? effect.reward_id
    return { type: 'reward', rewardId: rewardId ? String(rewardId) : null }
  }
  if (effect && EFFECT_TYPES.includes(effect.type)) {
    const out = { type: effect.type, value: toNumberOrNull(effect.value) }
    if (effect.type === 'points_per_amount') out.perAmount = toNumberOrNull(effect.perAmount)
    // Multiplicador solo sobre ciertos productos.
    if (effect.type === 'points_multiplier' && isPlainObject(effect.items) && stringList(effect.items.values).length > 0) {
      out.items = { by: String(effect.items.by || 'id'), values: stringList(effect.items.values) }
    }
    return out
  }
  // Regla anterior: `prizeType: 'points'` (o `{ id: 'points' }`) con `points: N`.
  const legacyType = isPlainObject(rule.prizeType) ? rule.prizeType.id : rule.prizeType
  if (legacyType === 'points' || rule.points !== undefined) {
    return { type: 'points_fixed', value: toNumberOrNull(rule.points) }
  }
  return { type: 'points_fixed', value: null }
}

// El formulario marca con `scoped` si el multiplicador es solo para ciertos productos.
function storedEffectSource(effect) {
  if (!isPlainObject(effect) || effect.scoped !== false) return effect
  return { ...effect, items: null }
}

export function newRule(name) {
  return {
    id: uuid(),
    name: name || '',
    status: 1,
    conditions: { type: 'and', children: [] },
    effect: { type: 'points_fixed', value: 100, perAmount: null, rewardId: null, scoped: false, items: { by: 'id', values: [] } },
    limits: { perMember: null },
    exclusive: false
  }
}

/** Regla guardada → regla para editar (`dropped`: condiciones que ya no se ofrecen). */
export function ruleToEditable(stored) {
  const source = stored || {}
  const { conditions, dropped } = conditionsToEditable(source.conditions)
  const effect = normalizeEffect(source)
  // El formulario tiene siempre todos los campos del efecto, se usen o no.
  if (effect.type !== 'points_per_amount') effect.perAmount = null
  if (effect.type !== 'reward') effect.rewardId = null
  else effect.value = null
  effect.scoped = !!effect.items
  if (!effect.items) effect.items = { by: 'id', values: [] }
  return {
    rule: {
      id: source.id || uuid(),
      name: source.name || '',
      status: Number(source.status) === 0 ? 0 : 1,
      conditions,
      effect,
      limits: { perMember: toNumberOrNull(source.limits && source.limits.perMember) },
      exclusive: source.exclusive === true
    },
    dropped
  }
}

/** Regla en el formato exacto que se guarda. */
export function ruleToStored(rule) {
  const source = rule || {}
  const perMember = toNumberOrNull(source.limits && source.limits.perMember)
  return {
    id: source.id || uuid(),
    name: String(source.name || '').trim(),
    status: Number(source.status) === 0 ? 0 : 1,
    conditions: conditionsToStored(source.conditions),
    effect: normalizeEffect({ ...source, effect: storedEffectSource(source.effect) }),
    limits: { perMember: perMember !== null && perMember >= 1 ? Math.floor(perMember) : null },
    exclusive: source.exclusive === true
  }
}

/** Todas las reglas de una campaña en el formato que se guarda (con `id` estable). */
export function rulesToStored(rules) {
  return (Array.isArray(rules) ? rules : []).map(ruleToStored)
}

/** Códigos de lo que le falta a una regla para poder guardarse. */
export function ruleProblems(rule) {
  const problems = []
  if (!String((rule && rule.name) || '').trim()) problems.push('name')
  if (hasIncompleteCondition(rule && rule.conditions)) problems.push('conditions')
  const effect = normalizeEffect({ ...(rule || {}), effect: storedEffectSource(rule && rule.effect) })
  if (effect.type === 'reward') {
    if (!effect.rewardId) problems.push('effect_reward')
  } else if (effect.value === null || effect.value <= 0) problems.push('effect_value')
  else if (effect.type === 'points_multiplier' && effect.value <= 1) problems.push('effect_multiplier')
  if (effect.type === 'points_multiplier' && rule && rule.effect && rule.effect.scoped === true && !effect.items) problems.push('effect_items')
  if (effect.type === 'points_per_amount' && (effect.perAmount === null || effect.perAmount <= 0)) problems.push('effect_per_amount')
  const perMember = rule && rule.limits ? rule.limits.perMember : null
  if (perMember !== null && perMember !== '' && perMember !== undefined && !(Number(perMember) >= 1)) problems.push('per_member')
  return problems
}

/* ───────────── Simulador ───────────── */

/** Campaña guardada (o en edición) → lo que recibe el simulador. */
export function campaignForSimulation(campaign) {
  const source = campaign || {}
  return {
    name: source.name || '',
    trigger: isPlainObject(source.trigger) ? source.trigger.id : (source.trigger || 'purchase'),
    event: String(source.event || '').trim().toLowerCase(),
    active: Number(source.active) === 0 ? 0 : 1,
    startDate: source.startDate ? new Date(source.startDate).toISOString() : null,
    endDate: source.endDate ? new Date(source.endDate).toISOString() : null,
    rules: rulesToStored(source.rules)
  }
}

export function levelsForSimulation(levels) {
  return (Array.isArray(levels) ? levels : [])
    .filter(level => level && Number(level.active) !== 0)
    .map(level => ({ _id: level._id, name: level.name, points: Number(level.points) || 0, settings: level.settings || {} }))
}

/* ───────────── Recompensas ───────────── */

export const REWARD_TYPES = ['amount', 'percent', 'product']
export const PERIOD_UNITS = ['day', 'week', 'month']
export const VOUCHER_STATUSES = ['active', 'held', 'used', 'expired', 'cancelled']

export function newReward(programId) {
  return {
    loyaltyProgram: programId || null,
    name: '',
    description: '',
    image: '',
    type: 'amount',
    value: null,
    maxDiscount: null,
    products: [],
    points: 0,
    levels: [],
    stock: null,
    limits: { perMember: null, perPeriodCount: null, perPeriodUnit: 'month' },
    validityDays: null,
    minOrderAmount: null,
    autoIssue: false,
    catalog: true,
    active: 1,
    startDate: null,
    endDate: null
  }
}

/** Recompensa guardada → formulario (todos los campos presentes). */
export function rewardToEditable(stored) {
  const source = stored || {}
  const limits = isPlainObject(source.limits) ? source.limits : {}
  const period = isPlainObject(limits.perPeriod) ? limits.perPeriod : {}
  return {
    ...newReward(source.loyaltyProgram),
    _id: source._id,
    name: source.name || '',
    description: source.description || '',
    image: source.image || '',
    type: REWARD_TYPES.includes(source.type) ? source.type : 'amount',
    value: toNumberOrNull(source.value),
    maxDiscount: toNumberOrNull(source.maxDiscount),
    products: (Array.isArray(source.products) ? source.products : []).map(product => ({
      id: String((isPlainObject(product) ? product.id : product) ?? ''),
      name: (isPlainObject(product) && product.name) || '',
      quantity: (isPlainObject(product) && Number(product.quantity)) || 1
    })),
    points: toNumberOrNull(source.points) || 0,
    levels: (Array.isArray(source.levels) ? source.levels : []).map(String),
    stock: toNumberOrNull(source.stock),
    limits: {
      perMember: toNumberOrNull(limits.perMember),
      perPeriodCount: toNumberOrNull(period.count),
      perPeriodUnit: PERIOD_UNITS.includes(period.unit) ? period.unit : 'month'
    },
    validityDays: toNumberOrNull(source.validityDays),
    minOrderAmount: toNumberOrNull(source.minOrderAmount),
    autoIssue: source.autoIssue === true,
    catalog: source.catalog !== false,
    active: Number(source.active) === 0 ? 0 : 1,
    startDate: source.startDate || null,
    endDate: source.endDate || null
  }
}

/** Formulario → recompensa en el formato que se guarda. */
export function rewardToStored(form) {
  const source = form || {}
  const limits = source.limits || {}
  const whole = value => {
    const number = toNumberOrNull(value)
    return number !== null && number >= 0 ? Math.floor(number) : null
  }
  const perPeriodCount = whole(limits.perPeriodCount)
  const out = {
    loyaltyProgram: isPlainObject(source.loyaltyProgram) ? source.loyaltyProgram._id : source.loyaltyProgram,
    name: String(source.name || '').trim(),
    description: String(source.description || '').trim(),
    image: String(source.image || '').trim(),
    type: source.type,
    value: source.type === 'product' ? null : toNumberOrNull(source.value),
    maxDiscount: source.type === 'percent' ? toNumberOrNull(source.maxDiscount) : null,
    products: source.type === 'product'
      ? (source.products || [])
        .filter(product => String(product.id || '').trim())
        .map(product => ({ id: String(product.id).trim(), name: String(product.name || '').trim(), quantity: Math.max(1, Math.floor(Number(product.quantity)) || 1) }))
      : [],
    points: whole(source.points) || 0,
    levels: (source.levels || []).map(level => String(isPlainObject(level) ? level._id : level)),
    stock: whole(source.stock),
    limits: {
      perMember: whole(limits.perMember) || null,
      perPeriod: perPeriodCount ? { count: perPeriodCount, unit: limits.perPeriodUnit || 'month' } : null
    },
    validityDays: whole(source.validityDays) || null,
    minOrderAmount: toNumberOrNull(source.minOrderAmount) || 0,
    autoIssue: source.autoIssue === true,
    catalog: source.catalog !== false,
    active: Number(source.active) === 0 ? 0 : 1,
    startDate: source.startDate || null,
    endDate: source.endDate || null
  }
  if (source._id) out._id = source._id
  return out
}

/** Lo que le falta a una recompensa para poder guardarse (códigos). */
export function rewardProblems(form) {
  const reward = rewardToStored(form)
  const problems = []
  if (!reward.name) problems.push('name')
  if (reward.type === 'amount' && !(reward.value > 0)) problems.push('value')
  if (reward.type === 'percent' && !(reward.value > 0 && reward.value <= 100)) problems.push('percent')
  if (reward.type === 'product' && reward.products.length === 0) problems.push('products')
  if (reward.autoIssue && !(reward.points > 0)) problems.push('auto_issue')
  return problems
}

/** Qué entrega una recompensa o un vale, en pocas palabras ("S/ 5 de descuento", "Palitos de ajo sin cargo"). */
export function rewardValueText(reward, settings, t, locale) {
  const source = reward || {}
  if (source.type === 'percent') {
    const max = source.maxDiscount ?? source.max_discount
    const text = t('loyalty.rewards.value_percent', { value: formatNumber(source.value, locale) })
    return max ? `${text} ${t('loyalty.rewards.value_max', { amount: formatMoney(max, settings, locale) })}` : text
  }
  if (source.type === 'product') {
    const names = (source.products || []).map(product => {
      const name = product.name || product.id
      return Number(product.quantity) > 1 ? `${product.quantity} × ${name}` : name
    })
    return t('loyalty.rewards.value_product', { products: names.join(', ') })
  }
  return t('loyalty.rewards.value_amount', { amount: formatMoney(source.value, settings, locale) })
}

export function rewardsForSimulation(rewards) {
  return (Array.isArray(rewards) ? rewards : []).map(reward => rewardToStored(rewardToEditable(reward)))
}
